import { useState } from "react";
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2, Lock } from "lucide-react";

/** Small presentational pieces shared by the builder steps. */

const METHOD_STYLES = {
  GET: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  POST: "bg-blue-50 text-blue-700 ring-blue-200",
  PUT: "bg-amber-50 text-amber-700 ring-amber-200",
  PATCH: "bg-violet-50 text-violet-700 ring-violet-200",
  DELETE: "bg-rose-50 text-rose-700 ring-rose-200"
};

export function MethodPill({ method, className = "" }) {
  return (
    <span
      className={`inline-flex w-16 justify-center rounded-md px-2 py-0.5 font-mono text-[11px] font-semibold ring-1 ring-inset ${
        METHOD_STYLES[method] || "bg-gray-50 text-gray-700 ring-gray-200"
      } ${className}`}
    >
      {method}
    </span>
  );
}

export function Field({ label, hint, error, success, optional, children }) {
  return (
    <label className="block">
      {(label || optional) && (
        <span className="mb-1.5 flex items-baseline justify-between">
          <span className="text-sm font-medium text-gray-900">{label}</span>
          {optional && <span className="text-xs text-gray-400">Optional</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1.5 block text-xs text-rose-600">{error}</span>
      ) : success ? (
        <span className="mt-1.5 block text-xs text-emerald-700">{success}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-xs text-gray-500">{hint}</span>
      ) : null}
    </label>
  );
}

export const inputClass = (invalid) =>
  `w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
    invalid
      ? "border-rose-300 focus:ring-rose-200"
      : "border-gray-300 focus:border-blue-600 focus:ring-blue-100"
  }`;

export function SecretInput({ value, onChange, placeholder }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={`${inputClass(false)} pl-9 pr-10 font-mono`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-gray-500 hover:bg-gray-100"
        aria-label={shown ? "Hide value" : "Show value"}
      >
        {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function Card({ title, subtitle, action, children, className = "" }) {
  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-6 ${className}`}
    >
      {(title || action) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {title && (
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            )}
            {subtitle && (
              <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
            )}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** A label and its explanation on the left, the control on the right; stacked on small screens. */
export function SettingRow({ title, hint, children }) {
  return (
    <div className="grid gap-3 p-6 md:grid-cols-[200px_minmax(0,1fr)] md:gap-8">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2 text-sm font-medium text-gray-700"
    >
      <span
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          checked ? "bg-blue-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </span>
      {label}
    </button>
  );
}

export function CodeBlock({ children }) {
  return (
    <pre className="overflow-x-auto rounded-xl bg-gray-900 p-4 font-mono text-xs leading-relaxed text-gray-100">
      {children}
    </pre>
  );
}

const STATUS_STYLES = {
  Live: ["bg-emerald-50 text-emerald-700 ring-emerald-200", "bg-emerald-500"],
  Draft: ["bg-gray-100 text-gray-700 ring-gray-200", "bg-gray-400"],
  Disabled: ["bg-amber-50 text-amber-800 ring-amber-200", "bg-amber-500"]
};

const STATUS_LABEL = { draft: "Draft", live: "Live", disabled: "Disabled" };

/**
 * One label per state, so every screen words it the same way. Integrations
 * carry a server-derived `status` (a disabled one is still published, with its
 * connection off); endpoints only have lifecycle_state.
 */
export const statusOf = ({ status, lifecycle_state, is_active }) =>
  STATUS_LABEL[status] ||
  (lifecycle_state === "draft" ? "Draft" : is_active ? "Live" : "Disabled");

export function StatusBadge({ status }) {
  const [badge, dot] = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
}

/** The integration's uploaded logo, or its first letter when it has none. */
export function IntegrationLogo({ name, logoUrl, className = "" }) {
  if (logoUrl) {
    return <img src={logoUrl} alt={`${name} logo`} className={`shrink-0 rounded-xl bg-white object-contain ${className}`} />;
  }
  return (
    <div className={`flex shrink-0 items-center justify-center rounded-xl bg-blue-600 font-semibold text-white ${className}`}>
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export function ErrorNote({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-4 flex items-start gap-2 text-sm text-rose-600">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      {message}
    </p>
  );
}

const NEXT_STYLES = {
  primary: "bg-blue-600 text-white shadow-sm hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none",
  // For a step whose own main action is the blue one (Try it's Send), so the two don't compete.
  secondary: "border border-gray-300 bg-white text-gray-900 shadow-sm hover:bg-gray-50 disabled:text-gray-400"
};

/** Back / primary action row shared by every step. */
export function StepFooter({
  onBack,
  onNext,
  nextLabel = "Continue",
  disabled,
  busy,
  icon: Icon = ArrowRight,
  hint,
  variant = "primary",
  className = "mt-8"
}) {
  // The manage view reuses the steps as tabs, which have nowhere to go back or on to.
  if (!onBack && !onNext) return null;
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <button
        type="button"
        onClick={onBack}
        className={`rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 ${onBack ? "" : "invisible"}`}
      >
        Back
      </button>
      <div className="flex items-center gap-3">
        {hint && <span className="text-xs text-gray-500">{hint}</span>}
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || busy}
          className={`flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${NEXT_STYLES[variant]}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {nextLabel}
          {!busy && <Icon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const PRIVATE_NAME = /^(localhost|.+\.localhost|.+\.local|.+\.internal)\.?$/i;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const PRIVATE_IPV4 =
  /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|169\.254\.|100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.|0\.)/;
// Loopback, unspecified, unique-local (fc00::/7) and link-local (fe80::/10).
const PRIVATE_IPV6 = /^(::1?|f[cd][0-9a-f]*:.*|fe[89ab][0-9a-f]*:.*)$/i;

/** Takes a URL hostname: IPv6 literals arrive bracketed, so only those are read as IPv6. */
export function isPrivateHost(hostname) {
  const host = (hostname || "").toLowerCase();
  if (host.includes(":")) return PRIVATE_IPV6.test(host.replace(/^\[|\]$/g, ""));
  if (IPV4.test(host)) return PRIVATE_IPV4.test(host);
  return PRIVATE_NAME.test(host);
}

/**
 * Mirrors the planned outbound URL rules (HTTPS, public internet only).
 * Returns { error } or { ok, warning? }. UI hint only — the server decides.
 */
export function checkBaseUrl(raw) {
  const value = (raw || "").trim();
  if (!value) return {};
  if (value.length > 2048) return { error: "The address is longer than 2,048 characters, the most Orbit accepts." };
  let url;
  try {
    url = new URL(value);
  } catch {
    return { error: "That doesn't look like a web address. Start with https://" };
  }
  if (url.protocol !== "https:") {
    return {
      error: "Use https:// — Orbit only calls APIs over a secure connection."
    };
  }
  if (isPrivateHost(url.hostname)) {
    return {
      error:
        "This address is on a private network. Orbit can only reach APIs on the public internet."
    };
  }
  if (url.port && url.port !== "443") {
    return { ok: true, warning: `Port ${url.port} needs to be allowed by Native Orange.` };
  }
  return { ok: true };
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const SPEC_EXTENSIONS = /\.(json|ya?ml)$/i;

/** Checked before reading, so a wrong file gets a clear message instead of a parse error. */
export function fileProblem(file) {
  if (!SPEC_EXTENSIONS.test(file.name)) {
    return `“${file.name}” isn't a .json, .yaml or .yml file. Export your API docs as OpenAPI first.`;
  }
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_FILE_BYTES) return "The file is larger than 5 MB.";
  return null;
}

/** Same rule as the server, so the path field explains a mistake as it is typed. */
export function checkPath(raw) {
  const path = (raw || "").trim();
  if (!path) return null;
  if (path.includes("://")) return "Enter only the path, e.g. /claims/{id}. The address is set on the Connection step.";
  if (!path.startsWith("/")) return "Start the path with /";
  if (/[?#]/.test(path)) return "Leave the ?query out; add query inputs below instead.";
  if (/\s/.test(path)) return "The path can't contain spaces.";
  const names = pathPlaceholders(path);
  if (new Set(names).size !== names.length) return "A {placeholder} appears twice.";
  return null;
}

/** Path placeholders like {quoteId} in "/quotes/{quoteId}". */
export const pathPlaceholders = (path) =>
  Array.from((path || "").matchAll(/\{([^}]+)\}/g), (m) => m[1]);

export const hostOf = (raw) => {
  try {
    return new URL(raw).host;
  } catch {
    return raw;
  }
};
